/**
 * Document Viewer Content Component
 * Switches between read-only viewer and editable StructuredEditor
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { documentsApi, companyDocumentsApi, referenceDocumentsApi, projectsApi } from '../../services/api';
import { logger } from '../../utils/logger';
import { StructuredEditor } from '../DocumentEditor/StructuredEditor';
import { convertTipTapToDocument } from '../../utils/tiptap-to-document';
import { exportToDocx } from '../../utils/docx-export';
import PdfViewer from './PdfViewer';
import DocumentControlBar from './DocumentControlBar';
import PlanControlBar from './PlanControlBar';
import LinkTargetDocumentModal from './LinkTargetDocumentModal';
import PDFExportButton from '../PDFExportButton';
import { useEditSession } from '../../hooks/useEditSession';
import { useAI } from '../../contexts/AIContext';
import type { AeroDocument } from '../../types/document-schema';
import type { DocumentOperation } from '../../types/document-operations';
import type { DocumentRevision, CompanyDocumentRevision } from '../../types';
import type { Editor } from '@tiptap/react';
import { Button, Spinner, useToast } from '@/components/ui';

// Type for other editors in concurrent editing
interface EditorUser {
  userId: string;
  userEmail?: string;
  lastActivity: string;
}

// Type for company document metadata
interface CompanyDocumentMeta {
  documentId: string;
  title: string;
  documentType?: string;
  status?: string;
  fileType?: string;
  s3Key?: string;
}

interface OpenedDocument {
  id: string;
  tabId: number;
  documentNumber: string;
  projectId?: string;
  title: string;
  type: 'project' | 'company' | 'standard' | 'prior-project' | 'plan';
  canEdit: boolean;
}

interface DocumentViewerContentProps {
  document: OpenedDocument;
  isEditing: boolean;
  onEnableEdit: () => void;
  onDisableEdit: (documentNumber?: string) => void;
  onBack?: () => void;  // Navigate back without closing tab
  onDocumentLoaded?: (jsonDocument: AeroDocument, version: string, s3VersionId?: string, s3JsonKey?: string) => void;
  onUpdate?: (operations: DocumentOperation[]) => void;
  onSave?: () => void;
  onDocumentOperation?: (operation: DocumentOperation) => void;
  onOpenPdfInViewer?: (pdfUrl: string, title: string) => void;
  // External sync: When AI updates the document via context, parent passes it here
  externalSyncDocument?: AeroDocument | null;
  // Plan execution: send message to AI and open target document
  onSendMessage?: (message: string, options?: { modeOverride?: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop' }) => void;
  onOpenDocument?: (doc: { documentNumber: string; projectId: string }) => void;
  // Set AI mode for plan execution
  onSetAiMode?: (mode: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop') => void;
}

export default function DocumentViewerContent({
  document,
  isEditing,
  onEnableEdit,
  onDisableEdit,
  onBack: _onBack,
  onDocumentLoaded,
  onUpdate: _onUpdate,
  onSave: _onSave,
  onDocumentOperation,
  onOpenPdfInViewer,
  externalSyncDocument,
  onSendMessage,
  onOpenDocument: _onOpenDocument,
  onSetAiMode,
}: DocumentViewerContentProps) {
  // Unused props kept for API compatibility
  void _onBack;
  void _onUpdate;
  void _onOpenDocument; // Target document is not auto-opened during plan execution to keep focus on plan
  // _onSave is used in handleSave
  
  // AIContext for document locking and linking during plan execution
  const { 
    acquireDocumentLock, 
    releaseDocumentLock, 
    activeConversationId,
    linkArtifact,
    unlinkArtifact: _unlinkArtifact, // Reserved for future: unlink on plan completion
  } = useAI();
  void _unlinkArtifact; // Suppress unused warning
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jsonDocument, setJsonDocument] = useState<AeroDocument | null>(null);
  const [_htmlContent, _setHtmlContent] = useState<string>('');
  void _htmlContent; // Used for potential HTML rendering
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [_s3VersionId, setS3VersionId] = useState<string | undefined>(undefined);
  const [s3JsonKey, setS3JsonKey] = useState<string | undefined>(undefined);
  void _s3VersionId; // Tracked for version comparison
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isOnline] = useState(true); // Could be enhanced with actual online detection
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Tracks whether AI streaming is in progress - prevents auto-save timers from starting
  // Set to true on first section_update, cleared on document_sync
  const isAIStreamingRef = useRef(false);
  const editorRef = useRef<Editor | null>(null);
  const [streamingOperations, setStreamingOperations] = useState<DocumentOperation[]>([]);
  const operationQueueRef = useRef<DocumentOperation[]>([]);
  
  // Document metadata for control bar (status, revisions)
  const [documentStatus, setDocumentStatus] = useState<string>('Draft');
  const [currentRevision, setCurrentRevision] = useState<string>('Draft');
  const [revisions, setRevisions] = useState<(DocumentRevision | CompanyDocumentRevision)[]>([]);
  
  // For company documents without JSON content (PDF or other format)
  const [companyDocMeta, setCompanyDocMeta] = useState<CompanyDocumentMeta | null>(null);
  
  // Concurrent edit detection
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [saveConflictError, setSaveConflictError] = useState<string | null>(null);
  
  // Export state for header export buttons
  const [exportingWord, setExportingWord] = useState(false);
  
  // Plan-specific state
  const [showLinkTargetModal, setShowLinkTargetModal] = useState(false);
  const [planProcessing, setPlanProcessing] = useState(false);
  
  const handleConflictDetected = useCallback((editors: EditorUser[]) => {
    if (editors.length > 0) {
      setShowConflictWarning(true);
    }
  }, []);
  
  // Stable ref for onDocumentLoaded to prevent infinite loops
  // The callback may change on parent re-renders but we don't want to reload document
  const onDocumentLoadedRef = useRef(onDocumentLoaded);
  useEffect(() => {
    onDocumentLoadedRef.current = onDocumentLoaded;
  }, [onDocumentLoaded]);
  
  // CRITICAL: Sync local state when parent passes updated document from AI (via context)
  // This is the reliable path for document_sync events - doesn't depend on window handlers
  useEffect(() => {
    if (externalSyncDocument && externalSyncDocument.version !== version) {
      logger.debug('[DocumentViewerContent] External sync received, updating local state:', {
        externalVersion: externalSyncDocument.version,
        currentVersion: version
      });
      setJsonDocument(externalSyncDocument);
      setVersion(externalSyncDocument.version);
      versionRef.current = externalSyncDocument.version;
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      // Clear any pending auto-save since document is now in sync
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    }
  }, [externalSyncDocument, version]);
  
  const { otherEditors, hasConflict } = useEditSession({
    projectId: document.projectId,
    documentNumber: document.documentNumber,
    isEditing,
    onConflictDetected: handleConflictDetected
  });
  
  // Plan document detection and task counts
  const isPlanDocument = useMemo(() => {
    return jsonDocument?.metadata?.documentType === 'ai_plan';
  }, [jsonDocument?.metadata?.documentType]);
  
  const { taskCount, completedTaskCount } = useMemo(() => {
    // body IS the sections array (DocumentSection[]), not body.sections
    if (!jsonDocument?.body || !Array.isArray(jsonDocument.body)) return { taskCount: 0, completedTaskCount: 0 };
    let total = 0;
    let completed = 0;
    for (const section of jsonDocument.body) {
      for (const block of section.blocks || []) {
        if (block.type === 'plan_task') {
          total++;
          if ((block.content as { status?: string })?.status === 'completed') {
            completed++;
          }
        }
      }
    }
    logger.debug('[DocumentViewerContent] Task count calculated:', { total, completed });
    return { taskCount: total, completedTaskCount: completed };
  }, [jsonDocument?.body]);
  
  const planStatus = useMemo(() => {
    return (jsonDocument?.metadata as { planStatus?: string })?.planStatus || 'draft';
  }, [jsonDocument?.metadata]);
  
  const targetDocument = useMemo(() => {
    const result = (jsonDocument?.metadata as { targetDocument?: { id: string; name: string; version?: string } })?.targetDocument || null;
    logger.debug('[DocumentViewerContent] targetDocument memo calculated:', result, 'from metadata:', jsonDocument?.metadata);
    return result;
  }, [jsonDocument?.metadata]);
  
  // Debug: Log PlanControlBar render conditions
  useEffect(() => {
    if (isPlanDocument) {
      logger.debug('[DocumentViewerContent] PlanControlBar conditions:', {
        isEditing,
        isPlanDocument,
        projectId: document.projectId,
        targetDocument,
        planStatus,
        taskCount,
        willRender: !isEditing && isPlanDocument && !!document.projectId
      });
    }
  }, [isEditing, isPlanDocument, document.projectId, targetDocument, planStatus, taskCount]);
  
  // Track previous planStatus to detect execution end
  const prevPlanStatusRef = useRef<string | null>(null);
  
  // Release lock when plan execution ends (status changes from 'executing' to anything else)
  useEffect(() => {
    const prevStatus = prevPlanStatusRef.current;
    prevPlanStatusRef.current = planStatus;
    
    // Detect when execution ends
    if (prevStatus === 'executing' && planStatus !== 'executing' && targetDocument) {
      logger.debug('[DocumentViewerContent] Plan execution ended, releasing lock on:', targetDocument.id);
      releaseDocumentLock(targetDocument.id);
      
      // Show appropriate toast based on final status
      if (planStatus === 'completed') {
        toast({
          title: 'Plan Completed',
          description: 'All tasks have been executed successfully.',
        });
      } else if (planStatus === 'paused') {
        toast({
          title: 'Plan Paused',
          description: 'Execution was stopped. You can resume later.',
        });
      } else if (planStatus === 'failed') {
        toast({
          variant: 'destructive',
          title: 'Plan Failed',
          description: 'Execution encountered an error.',
        });
      }
    }
  }, [planStatus, targetDocument, releaseDocumentLock, toast]);
  
  // Cleanup: Release lock when component unmounts while executing
  useEffect(() => {
    return () => {
      // Use ref values since state may be stale in cleanup
      if (prevPlanStatusRef.current === 'executing' && targetDocument) {
        logger.debug('[DocumentViewerContent] Unmounting during execution, releasing lock on:', targetDocument.id);
        releaseDocumentLock(targetDocument.id);
      }
    };
  }, [targetDocument, releaseDocumentLock]);
  
  // Expose method to queue streaming operations
  // Parent (WorkspacePage) calls this when WebSocket receives document_operation event
  useEffect(() => {
    if (onDocumentOperation) {
      // Store the handler so parent can call it
      window.__queueDocumentOperation = (operation: DocumentOperation) => {
        logger.debug('[DocumentViewerContent] Queueing streaming operation:', operation.op);
        operationQueueRef.current.push(operation);
        setStreamingOperations([...operationQueueRef.current]);
      };
      
      // Async version that waits for operation to complete
      // Used by Plan mode execution to ensure sequential application
      window.__applyDocumentOperationAsync = async (operation: DocumentOperation): Promise<void> => {
        return new Promise((resolve) => {
          logger.debug('[DocumentViewerContent] Applying operation async:', operation.op);
          
          // Clear the queue first to avoid re-applying old operations
          operationQueueRef.current = [operation];
          setStreamingOperations([operation]);
          
          // Wait for the operation to be processed
          // The StructuredEditor will call onStreamingComplete when done
          const checkComplete = setInterval(() => {
            if (operationQueueRef.current.length === 0) {
              clearInterval(checkComplete);
              logger.debug('[DocumentViewerContent] Operation complete:', operation.op);
              resolve();
            }
          }, 50);
          
          // Timeout after 5 seconds
          setTimeout(() => {
            clearInterval(checkComplete);
            logger.warn('[DocumentViewerContent] Operation timeout:', operation.op);
            resolve();
          }, 5000);
        });
      };
    }
    
    // AI streaming coordination: called by useDocumentStreaming when section_update/document_sync arrive
    // Clears pre-existing auto-save timers and prevents new ones during AI streaming
    window.__setDocumentAIStreaming = (streaming: boolean) => {
      const wasStreaming = isAIStreamingRef.current;
      isAIStreamingRef.current = streaming;
      
      if (streaming && !wasStreaming) {
        // AI streaming just started - clear any pending auto-save timer from user edits
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
          logger.debug('[DocumentViewerContent] Cleared pending auto-save timer (AI streaming started)');
        }
        logger.debug('[DocumentViewerContent] AI streaming started - auto-save timers suppressed');
      } else if (!streaming && wasStreaming) {
        logger.debug('[DocumentViewerContent] AI streaming ended - auto-save timers re-enabled');
      }
    };
    
    // Expose method to get current document state for AI context
    window.__getCurrentEditorDocument = (): AeroDocument | null => {
      if (editorRef.current && jsonDocument) {
        try {
          const currentDoc = convertTipTapToDocument(editorRef.current, jsonDocument);
          logger.debug('[DocumentViewerContent] Extracted current editor state for AI');
          return currentDoc;
        } catch (error) {
          logger.error('[DocumentViewerContent] Failed to extract current editor state:', error);
          return jsonDocument; // Fallback to original
        }
      }
      return null;
    };
    
    return () => {
      delete window.__queueDocumentOperation;
      delete window.__applyDocumentOperationAsync;
      delete window.__setDocumentAIStreaming;
      delete window.__getCurrentEditorDocument;
    };
  }, [onDocumentOperation, jsonDocument]);

  const loadDocument = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Check if this is a design document (stored externally, not as JSON)
      const isDesignDoc = document.id?.startsWith('design-');
      
      if (isDesignDoc) {
        // Design documents are external files - no built-in viewer
        setLoading(false);
        return;
      }
      
      if (document.type === 'project' && document.projectId && document.documentNumber) {
        // Certification documents - JSON format
        // First fetch document metadata
        interface DocumentMeta {
          status?: string;
          currentRevision?: string;
          revisions?: Array<{ revisionId: string; version: string }>;
        }
        let docMeta: DocumentMeta | null = null;
        try {
          docMeta = await projectsApi.getDocument(document.projectId, document.documentNumber);
          setDocumentStatus(docMeta.status || 'Draft');
          setCurrentRevision(docMeta.currentRevision || 'Draft');
          setRevisions(docMeta.revisions || []);
        } catch (metaErr) {
          logger.warn('Could not load document metadata:', metaErr);
        }
        
        // Try to load existing JSON document with s3VersionId for undo functionality
        const loadResult = await documentsApi.loadAsJsonWithMetadata(document.projectId, document.documentNumber);
        let jsonDoc = loadResult.jsonDocument;
        const loadedS3VersionId = loadResult.s3VersionId;
        const loadedS3JsonKey = loadResult.s3JsonKey;
        
        // If no JSON document exists, create a blank template
        if (!jsonDoc) {
          logger.debug(`[DocumentViewerContent] No JSON document found for ${document.documentNumber}, creating blank template`);
          const now = new Date().toISOString();
          jsonDoc = {
            docId: document.documentNumber,
            schemaVersion: 'aero-doc-v1',
            version: now,
            metadata: {
              documentNumber: document.documentNumber,
              documentTitle: docMeta?.documentTitle || document.title,
              projectSpecificTitle: docMeta?.projectSpecificTitle || document.title,
              docType: docMeta?.documentType || docMeta?.docType || 'Document',
              currentRevision: docMeta?.currentRevision || 'Draft',
              status: docMeta?.status || 'Draft',
              revisionDate: now.split('T')[0],
              author: 'System',
              projectId: document.projectId,
              projectCode: docMeta?.projectCode || '',
              projectTitle: docMeta?.projectTitle || '',
            },
            frontMatter: [],
            body: [
              {
                id: 'sec-1',
                number: '1',
                title: 'Introduction',
                type: 'requirement' as const,
                parent: undefined,
                children: [],
                blocks: [
                  {
                    id: 'blk-1',
                    type: 'paragraph' as const,
                    content: {
                      text: 'Enter document content here.',
                    },
                    metadata: {
                      createdAt: now,
                      modifiedAt: now,
                      version: 1,
                    },
                  },
                ],
                required: false,
              },
            ],
            backMatter: [],
            lastModified: now,
            modifiedBy: 'System',
          } as AeroDocument;
        }
        
        // Reconcile JSON document metadata with DynamoDB record (source of truth).
        // The S3 JSON may contain stale metadata (e.g. after a revision migration).
        if (docMeta && jsonDoc.metadata) {
          if (docMeta.currentRevision && jsonDoc.metadata.currentRevision !== docMeta.currentRevision) {
            logger.debug(`[DocumentViewerContent] Reconciling revision: JSON has '${jsonDoc.metadata.currentRevision}', DB has '${docMeta.currentRevision}'`);
            jsonDoc.metadata.currentRevision = docMeta.currentRevision;
          }
          if (docMeta.status && jsonDoc.metadata.status !== docMeta.status) {
            logger.debug(`[DocumentViewerContent] Reconciling status: JSON has '${jsonDoc.metadata.status}', DB has '${docMeta.status}'`);
            jsonDoc.metadata.status = docMeta.status;
          }
        }

        logger.debug('[DocumentViewerContent] loadDocument: setting jsonDocument with metadata:', jsonDoc.metadata);
        logger.debug('[DocumentViewerContent] loadDocument: targetDocument in metadata:', (jsonDoc.metadata as { targetDocument?: unknown })?.targetDocument);
        setJsonDocument(jsonDoc);
        setVersion(jsonDoc.version || '1.0');
        setS3VersionId(loadedS3VersionId);
        setS3JsonKey(loadedS3JsonKey);
        
        // Use metadata from JSON document if we couldn't load it earlier
        if (!docMeta) {
          setDocumentStatus(jsonDoc.metadata?.status || 'Draft');
          setCurrentRevision(jsonDoc.metadata?.currentRevision || 'Draft');
        }
        
        if (onDocumentLoaded) {
          onDocumentLoadedRef.current?.(jsonDoc, jsonDoc.version || '1.0', loadedS3VersionId, loadedS3JsonKey);
        }
      } else if (document.type === 'company' && document.documentNumber) {
        // Company documents - First get metadata to check format
        const docMeta = await companyDocumentsApi.get(document.documentNumber, true);
        
        // Check if document has JSON content
        if (docMeta.jsonDocument) {
          const jsonDoc = docMeta.jsonDocument;
          const companyS3VersionId = docMeta.s3VersionId;

          // Reconcile JSON document metadata with DynamoDB record (source of truth)
          if (jsonDoc.metadata) {
            if (docMeta.currentRevision && jsonDoc.metadata.currentRevision !== docMeta.currentRevision) {
              logger.debug(`[DocumentViewerContent] Company doc: reconciling revision '${jsonDoc.metadata.currentRevision}' -> '${docMeta.currentRevision}'`);
              jsonDoc.metadata.currentRevision = docMeta.currentRevision;
            }
            if (docMeta.status && jsonDoc.metadata.status !== docMeta.status) {
              logger.debug(`[DocumentViewerContent] Company doc: reconciling status '${jsonDoc.metadata.status}' -> '${docMeta.status}'`);
              jsonDoc.metadata.status = docMeta.status;
            }
          }

          setJsonDocument(jsonDoc);
          setVersion(jsonDoc.version || '1.0');
          setS3VersionId(companyS3VersionId);
          setDocumentStatus(docMeta.status || 'Draft');
          setCurrentRevision(docMeta.currentRevision || 'Draft');
          setRevisions(docMeta.revisions || []);
          
          if (onDocumentLoaded) {
            onDocumentLoadedRef.current?.(jsonDoc, jsonDoc.version || '1.0', companyS3VersionId, docMeta.s3JsonKey);
          }
        } else if (docMeta.format === 'pdf' || docMeta.s3Key?.endsWith('.pdf')) {
          // PDF company document - try to get PDF URL
          try {
            // Construct PDF URL from S3 key if available
            if (docMeta.pdfUrl) {
              setPdfUrl(docMeta.pdfUrl);
            } else {
              // Fall back to showing info message for PDFs without URL
              setCompanyDocMeta(docMeta);
            }
            setDocumentStatus(docMeta.status || 'Draft');
            setCurrentRevision(docMeta.currentRevision || 'Draft');
            setRevisions(docMeta.revisions || []);
          } catch (pdfErr) {
            logger.warn('Could not load company document PDF:', pdfErr);
            setCompanyDocMeta(docMeta);
          }
        } else {
          // No JSON and not PDF - show info view with metadata
          setCompanyDocMeta(docMeta);
          setDocumentStatus(docMeta.status || 'Draft');
          setCurrentRevision(docMeta.currentRevision || 'Draft');
          setRevisions(docMeta.revisions || []);
        }
      } else if (document.type === 'standard' && document.documentNumber) {
        // Reference documents - PDFs
        try {
          const url = await referenceDocumentsApi.getPdfUrl(document.documentNumber);
          setPdfUrl(url);
        } catch (err: unknown) {
          logger.error('Failed to load PDF URL:', err);
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        }
      } else {
        setError('Unknown document type');
      }
    } catch (err: unknown) {
      logger.error('Failed to load document:', err);
      setError(err instanceof Error ? err.message : 'Failed to load document');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- document.title is not needed for re-fetching logic
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onDocumentLoadedRef is stable, don't include callback prop
  }, [document.id, document.type, document.projectId, document.documentNumber]);

  // Load document content
  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Warn about unsaved changes before navigating away
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const handleUpdate = (_operations: DocumentOperation[]) => {
    // During AI streaming, the server owns document state and saves are handled server-side.
    // Don't start auto-save timers that could fire mid-stream with partial content.
    if (isAIStreamingRef.current) {
      logger.debug('[DocumentViewerContent] handleUpdate skipped: AI streaming in progress');
      return;
    }
    
    logger.debug('[DocumentViewerContent] handleUpdate called - marking as unsaved');
    // Set unsaved changes flag - update both state AND ref immediately
    setHasUnsavedChanges(true);
    hasUnsavedChangesRef.current = true; // Immediate update for timer callback
    
    // Trigger auto-save after 3 seconds of inactivity
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      logger.debug('[DocumentViewerContent] Auto-save timer fired');
      handleSave();
    }, 3000); // Auto-save after 3 seconds of no changes (increased for better UX)
  };

  // Use refs to track current values to avoid stale closure issues
  const versionRef = useRef(version);
  useEffect(() => {
    versionRef.current = version;
  }, [version]);
  
  // CRITICAL: Track hasUnsavedChanges with ref to avoid stale closure in auto-save timer
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const handleSave = async () => {
    // Use ref to get current value (avoids stale closure from timer)
    const currentHasUnsavedChanges = hasUnsavedChangesRef.current;
    
    logger.debug('[DocumentViewerContent] handleSave called, checks:', {
      hasUnsavedChanges: currentHasUnsavedChanges,
      hasEditor: !!editorRef.current,
      hasJsonDocument: !!jsonDocument
    });
    if (!currentHasUnsavedChanges || !editorRef.current || !jsonDocument) {
      logger.debug('[DocumentViewerContent] handleSave returning early - condition not met');
      return;
    }
    
    // Use ref to get current version (avoids stale closure)
    const currentVersion = versionRef.current;
    
    // SAFEGUARD: Prevent saving blank template over existing content
    // If document has only 1 section with default text, it's likely a blank template
    // that was created because the actual document couldn't be loaded
    // 
    // IMPORTANT: Only block if the document EXISTS in S3 (has s3VersionId).
    // If s3VersionId is null/undefined, this is a NEW document that should be saved.
    const isBlankTemplate = jsonDocument.body?.length === 1 && 
      jsonDocument.body[0]?.blocks?.length === 1 &&
      jsonDocument.body[0]?.blocks[0]?.content?.text === 'Enter document content here.';
    
    // Only block if blank template would OVERWRITE an existing document in S3
    if (isBlankTemplate && _s3VersionId) {
      logger.warn('[DocumentViewerContent] BLOCKED: Attempted to save blank template over existing document');
      logger.warn('[DocumentViewerContent] This usually means the document failed to load properly. Reload the page.');
      setHasUnsavedChanges(false); // Clear flag to prevent retry
      return;
    }
    
    setSaving(true);
    setHasUnsavedChanges(false);
    
    try {
      logger.debug('[DocumentViewerContent] Saving document...');
      logger.debug('[DocumentViewerContent] Using version for save:', currentVersion);
      
      // Convert TipTap content back to AeroDocument JSON
      const updatedDocument = convertTipTapToDocument(editorRef.current, jsonDocument);
      
      // Save to backend based on document type
      // Include expectedVersion for optimistic locking
      if (document.type === 'project' && document.projectId && document.documentNumber) {
        // Use save content API with JSON document and version check
        const response = await documentsApi.saveContent(
          document.projectId, 
          document.documentNumber, 
          '', // content field not used for JSON documents
          { 
            jsonDocument: updatedDocument,
            expectedVersion: currentVersion // For optimistic locking
          }
        );
        logger.debug('[DocumentViewerContent] Project document saved successfully');
        
        // CRITICAL: Use server's returned document (has correct version from cache)
        // The server generates a new version and updates cache - we must use that exact version
        const serverDoc = response?.jsonDocument || updatedDocument;
        const newVersion = serverDoc.version || response?.version || updatedDocument.version;
        
        // Tell editor to skip refresh on version change (preserves cursor position)
        // This is a local save - content hasn't changed, only version tracking
        const editorWithSkip = editorRef.current as Editor & { skipNextVersionRefresh?: () => void };
        if (editorWithSkip?.skipNextVersionRefresh) {
          editorWithSkip.skipNextVersionRefresh();
        }
        
        // Update local state with server's document
        setJsonDocument(serverDoc);
        setVersion(newVersion);
        versionRef.current = newVersion;
        
        // Update s3VersionId for undo functionality
        const newS3VersionId = response?.s3VersionId;
        if (newS3VersionId) {
          setS3VersionId(newS3VersionId);
          logger.debug('[DocumentViewerContent] Updated s3VersionId:', newS3VersionId);
        }
        
        // Notify parent to update openedDocuments (keeps context in sync)
        if (onDocumentLoaded) {
          onDocumentLoadedRef.current?.(serverDoc, newVersion, newS3VersionId, s3JsonKey);
        }
        logger.debug('[DocumentViewerContent] Synced to server version:', newVersion);
      } else if (document.type === 'company' && document.documentNumber) {
        // Company documents save via PATCH with jsonDocument
        await companyDocumentsApi.update(document.documentNumber, { 
          jsonDocument: updatedDocument 
        });
        logger.debug('[DocumentViewerContent] Company document saved successfully');
        
        // Tell editor to skip refresh on version change (preserves cursor position)
        const editorWithSkipCompany = editorRef.current as Editor & { skipNextVersionRefresh?: () => void };
        if (editorWithSkipCompany?.skipNextVersionRefresh) {
          editorWithSkipCompany.skipNextVersionRefresh();
        }
        
        setJsonDocument(updatedDocument);
        setVersion(updatedDocument.version);
        versionRef.current = updatedDocument.version;
      }
      
      // Mark as saved (jsonDocument already updated in the type-specific block above)
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      // Call parent save callback if provided
      if (_onSave) {
        await _onSave();
      }
    } catch (err: unknown) {
      // Type-safe access to axios error response
      const axiosError = err as { response?: { status?: number; data?: { code?: string; currentDocument?: AeroDocument } }; message?: string };
      
      // Handle 409 Conflict - check for AI edit conflict first (silent handling)
      if (axiosError.response?.status === 409) {
        const responseData = axiosError.response.data;
        
        // AI edit conflict - sync to server version silently (not an error)
        if (responseData?.code === 'AI_EDIT_CONFLICT' && responseData?.currentDocument) {
          logger.debug('[DocumentViewerContent] AI edit conflict - syncing to server version');
          const serverDoc = responseData.currentDocument;
          
          // Update local state with server's document
          setJsonDocument(serverDoc);
          setVersion(serverDoc.version);
          versionRef.current = serverDoc.version;
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
          
          // Notify parent of updated document
          if (onDocumentLoadedRef.current) {
            onDocumentLoadedRef.current(serverDoc, serverDoc.version, undefined, s3JsonKey);
          }
          
          // Not an error - expected during AI editing
          return;
        }
        
        // Regular version conflict (another user) - log this as error
        logger.error('[DocumentViewerContent] Save failed - version conflict:', err);
        setHasUnsavedChanges(true);
        setSaveConflictError('Document was modified by another user. Please reload the page to see the latest version.');
        setShowConflictWarning(true);
      } else {
        // Other errors - log as error
        logger.error('[DocumentViewerContent] Save failed:', err);
        setHasUnsavedChanges(true);
        setSaveConflictError(axiosError.message || 'Failed to save document');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDisableEdit = () => {
    // With auto-save enabled, changes are saved automatically after 1-2 seconds
    // If there are pending changes, save them before exiting edit mode
    if (hasUnsavedChanges && editorRef.current && jsonDocument) {
      // Trigger immediate save before exiting
      handleSave();
    }
    
    // Clear any pending auto-save timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    setHasUnsavedChanges(false);
    onDisableEdit(document.documentNumber);
  };

  // Handle Word export from header
  // Pre-renders diagrams as PNG on the server for reliable embedding
  const handleExportWord = async () => {
    if (!jsonDocument || !document.projectId) return;
    setExportingWord(true);
    try {
      // Check if document has any vector_graphic blocks that need pre-rendering
      // Simple check: scan ALL sections in body for any vector_graphic blocks
      const hasDiagrams = jsonDocument.body?.some(section => 
        section.blocks?.some(block => block.type === 'vector_graphic')
      ) || false;
      logger.debug('[Word Export] Document has diagrams:', hasDiagrams);
      
      let documentToExport = jsonDocument;
      
      // Always call prepare-export to pre-render diagrams if they exist
      // This ensures the PNG data is available for embedding
      if (hasDiagrams) {
        logger.debug('[Word Export] Calling prepare-export API to render diagrams as PNG...');
        try {
          documentToExport = await documentsApi.prepareExport(
            document.projectId,
            document.documentNumber
          );
          logger.debug('[Word Export] Diagrams pre-rendered successfully');
          
          // Log to verify PNG data is present
          const renderedCount = documentToExport.body?.reduce((count, section) => {
            return count + (section.blocks?.filter(
              (b) => b.type === 'vector_graphic' && (b.content as Record<string, unknown>)?._renderedPngBase64
            ).length || 0);
          }, 0) || 0;
          logger.debug(`[Word Export] Found ${renderedCount} diagrams with PNG data`);
        } catch (prepareErr) {
          logger.error('[Word Export] Failed to prepare diagrams:', prepareErr);
          // Continue with original document - will show placeholder
        }
      }
      
      await exportToDocx(documentToExport);
    } catch (err) {
      logger.error('Word export failed:', err);
    } finally {
      setExportingWord(false);
    }
  };

  // Plan control handlers
  const handleLinkTargetDocument = async (target: { id: string; name: string }) => {
    if (!document.projectId || !jsonDocument) return;
    setPlanProcessing(true);
    try {
      // Update the plan's jsonDocument metadata with targetDocument
      // Keep the existing version for conflict detection - backend will assign new version
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          targetDocument: {
            id: target.id,
            name: target.name,
          },
        },
      };
      
      // Save the updated document to S3
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      
      // Reload to get updated state
      await loadDocument();
      logger.debug('[DocumentViewerContent] Linked target document:', target.name);
    } catch (err) {
      logger.error('Failed to link target document:', err);
    } finally {
      setPlanProcessing(false);
      setShowLinkTargetModal(false);
    }
  };

  const handleUnlinkTargetDocument = async () => {
    if (!document.projectId || !jsonDocument) return;
    setPlanProcessing(true);
    try {
      // Remove targetDocument from metadata
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          targetDocument: undefined,
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      
      await loadDocument();
      logger.debug('[DocumentViewerContent] Unlinked target document');
    } catch (err) {
      logger.error('Failed to unlink target document:', err);
    } finally {
      setPlanProcessing(false);
    }
  };

  const handleMarkReady = async () => {
    if (!document.projectId || !jsonDocument) return;
    setPlanProcessing(true);
    try {
      // Update the plan's jsonDocument metadata with planStatus
      // Keep existing version for conflict detection - backend assigns new version
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          planStatus: 'ready' as const,
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      
      await loadDocument();
      logger.debug('[DocumentViewerContent] Plan marked as ready');
    } catch (err) {
      logger.error('Failed to mark plan as ready:', err);
    } finally {
      setPlanProcessing(false);
    }
  };

  const handleRevokeReady = async () => {
    if (!document.projectId || !jsonDocument) return;
    setPlanProcessing(true);
    try {
      // Move plan back to draft status for editing
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          planStatus: 'draft' as const,
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      
      await loadDocument();
      logger.debug('[DocumentViewerContent] Plan revoked to draft');
    } catch (err) {
      logger.error('Failed to revoke plan ready status:', err);
    } finally {
      setPlanProcessing(false);
    }
  };

  const handleExecutePlan = async () => {
    if (!jsonDocument || !document.projectId) return;
    
    logger.debug('[DocumentViewerContent] Execute plan:', document.documentNumber);
    
    // Phase 1: Single target document
    // Future: metadata.targetDocuments[] for multi-document plans
    const targetDoc = (jsonDocument.metadata as { targetDocument?: { id: string; name: string } })?.targetDocument;
    if (!targetDoc) {
      toast({
        variant: 'destructive',
        title: 'No Target Document',
        description: 'Please link a target document before executing the plan.',
      });
      return;
    }
    
    if (!activeConversationId) {
      toast({
        variant: 'destructive',
        title: 'No Active Conversation',
        description: 'Please start a conversation before executing the plan.',
      });
      return;
    }
    
    if (!onSendMessage) {
      logger.error('[DocumentViewerContent] onSendMessage prop not provided');
      toast({
        variant: 'destructive',
        title: 'Configuration Error',
        description: 'Plan execution is not properly configured.',
      });
      return;
    }
    
    // Validate projectId before execution - required for S3 document location
    if (!document.projectId) {
      logger.error('[DocumentViewerContent] Plan document has no projectId:', document);
      toast({
        variant: 'destructive',
        title: 'Missing Project Context',
        description: 'Plan document is not associated with a project. Please ensure the plan was created within a project.',
      });
      return;
    }
    
    setPlanProcessing(true);
    
    try {
      // 1. Acquire lock on target document(s)
      // Future: loop through targetDocuments[], acquire all locks atomically
      const lock = await acquireDocumentLock(targetDoc.id, activeConversationId, `Plan Execution: ${document.documentNumber}`);
      if (!lock) {
        toast({
          variant: 'destructive',
          title: 'Lock Failed',
          description: 'Target document is being edited by another user or conversation.',
        });
        return;
      }
      logger.debug('[DocumentViewerContent] Acquired lock on target document:', targetDoc.id);
      
      // 2. Note: We don't auto-open the target document to keep focus on the plan
      // The user can manually open it if they want to see changes in real-time
      
      // 3. Link plan document to conversation (required for execute_plan tool to find it)
      logger.debug('[DocumentViewerContent] Linking plan with projectId:', document.projectId);
      await linkArtifact('plan', document.documentNumber, document.title, document.projectId);
      logger.debug('[DocumentViewerContent] Linked plan document to conversation');
      
      // 4. Update plan status to 'executing'
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          planStatus: 'executing' as const,
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      await loadDocument();
      logger.debug('[DocumentViewerContent] Plan status set to executing');
      
      // 6. Switch AI to plan mode (required for execute_plan tool)
      // Also pass mode override to ensure the message uses plan mode
      // (React state updates are async, so the mode might not have propagated yet)
      if (onSetAiMode) {
        onSetAiMode('plan');
        logger.debug('[DocumentViewerContent] Switched AI to plan mode');
      }
      
      // 7. Send message to AI to start execution with explicit mode override
      onSendMessage(`Execute plan ${document.documentNumber}. Start with the execute_plan tool.`, { modeOverride: 'plan' });
      
    } catch (err) {
      logger.error('Failed to start plan execution:', err);
      // Release lock on failure
      if (targetDoc) {
        releaseDocumentLock(targetDoc.id);
      }
      toast({
        variant: 'destructive',
        title: 'Execution Failed',
        description: err instanceof Error ? err.message : 'Failed to start plan execution.',
      });
    } finally {
      setPlanProcessing(false);
    }
  };

  const handlePausePlan = async () => {
    if (!document.projectId || !jsonDocument) return;
    setPlanProcessing(true);
    try {
      // Keep existing version for conflict detection - backend assigns new version
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          planStatus: 'paused' as const,
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      
      await loadDocument();
      logger.debug('[DocumentViewerContent] Plan paused');
    } catch (err) {
      logger.error('Failed to pause plan:', err);
    } finally {
      setPlanProcessing(false);
    }
  };

  const handleResumePlan = async () => {
    // Resume continues execution from where it was paused
    logger.debug('[DocumentViewerContent] Resume plan:', document.documentNumber);
    
    if (!jsonDocument || !document.projectId) return;
    
    const targetDoc = (jsonDocument.metadata as { targetDocument?: { id: string; name: string } })?.targetDocument;
    if (!targetDoc) {
      toast({
        variant: 'destructive',
        title: 'No Target Document',
        description: 'Please link a target document before resuming the plan.',
      });
      return;
    }
    
    if (!activeConversationId) {
      toast({
        variant: 'destructive',
        title: 'No Active Conversation',
        description: 'Please start a conversation before resuming the plan.',
      });
      return;
    }
    
    if (!onSendMessage) {
      logger.error('[DocumentViewerContent] onSendMessage prop not provided');
      toast({
        variant: 'destructive',
        title: 'Configuration Error',
        description: 'Plan resume is not properly configured.',
      });
      return;
    }
    
    setPlanProcessing(true);
    
    try {
      // 1. Acquire lock on target document
      const lock = await acquireDocumentLock(targetDoc.id, activeConversationId, `Plan Execution: ${document.documentNumber}`);
      if (!lock) {
        toast({
          variant: 'destructive',
          title: 'Lock Failed',
          description: 'Target document is being edited by another user or conversation.',
        });
        return;
      }
      logger.debug('[DocumentViewerContent] Acquired lock on target document:', targetDoc.id);
      
      // 2. Link plan document to conversation (required for execute_plan tool)
      if (document.projectId) {
        await linkArtifact('plan', document.documentNumber, document.title, document.projectId);
        logger.debug('[DocumentViewerContent] Linked plan document to conversation (resume)');
      }
      
      // 3. Update plan status to 'executing'
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          planStatus: 'executing' as const,
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      await loadDocument();
      logger.debug('[DocumentViewerContent] Plan status set to executing (resumed)');
      
      // 4. Switch AI to plan mode
      if (onSetAiMode) {
        onSetAiMode('plan');
        logger.debug('[DocumentViewerContent] Switched AI to plan mode');
      }
      
      // 5. Send resume message to AI with mode override
      onSendMessage(`Resume plan ${document.documentNumber}. Continue from where it was paused using the execute_plan tool.`, { modeOverride: 'plan' });
      
    } catch (err) {
      logger.error('Failed to resume plan:', err);
      if (targetDoc) {
        releaseDocumentLock(targetDoc.id);
      }
      toast({
        variant: 'destructive',
        title: 'Resume Failed',
        description: err instanceof Error ? err.message : 'Failed to resume plan execution.',
      });
    } finally {
      setPlanProcessing(false);
    }
  };

  const handleRetryPlan = async () => {
    // Retry executes the plan again from the beginning
    // Self-contained like handleResumePlan - don't delegate to handleExecutePlan
    logger.debug('[DocumentViewerContent] Retry plan:', document.documentNumber);
    
    if (!jsonDocument || !document.projectId) return;
    
    const targetDoc = (jsonDocument.metadata as { targetDocument?: { id: string; name: string } })?.targetDocument;
    if (!targetDoc) {
      toast({
        variant: 'destructive',
        title: 'No Target Document',
        description: 'Please link a target document before retrying the plan.',
      });
      return;
    }
    
    if (!activeConversationId) {
      toast({
        variant: 'destructive',
        title: 'No Active Conversation',
        description: 'Please start a conversation before retrying the plan.',
      });
      return;
    }
    
    if (!onSendMessage) {
      logger.error('[DocumentViewerContent] onSendMessage prop not provided');
      toast({
        variant: 'destructive',
        title: 'Configuration Error',
        description: 'Plan retry is not properly configured.',
      });
      return;
    }
    
    setPlanProcessing(true);
    
    try {
      // 1. Acquire lock on target document
      const lock = await acquireDocumentLock(targetDoc.id, activeConversationId, `Plan Execution: ${document.documentNumber}`);
      if (!lock) {
        toast({
          variant: 'destructive',
          title: 'Lock Failed',
          description: 'Target document is being edited by another user or conversation.',
        });
        return;
      }
      logger.debug('[DocumentViewerContent] Acquired lock on target document:', targetDoc.id);
      
      // 2. Link plan document to conversation (required for execute_plan tool)
      await linkArtifact('plan', document.documentNumber, document.title, document.projectId);
      logger.debug('[DocumentViewerContent] Linked plan document to conversation (retry)');
      
      // 3. Update plan status directly to 'executing' (skip 'ready' state)
      const updatedDocument = {
        ...jsonDocument,
        metadata: {
          ...jsonDocument.metadata,
          planStatus: 'executing' as const,
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      await loadDocument();
      logger.debug('[DocumentViewerContent] Plan status set to executing (retry)');
      
      // 4. Switch AI to plan mode
      if (onSetAiMode) {
        onSetAiMode('plan');
        logger.debug('[DocumentViewerContent] Switched AI to plan mode');
      }
      
      // 5. Send retry message to AI with mode override
      onSendMessage(`Retry plan ${document.documentNumber}. Start fresh from the beginning using the execute_plan tool.`, { modeOverride: 'plan' });
      
    } catch (err) {
      logger.error('Failed to retry plan:', err);
      if (targetDoc) {
        releaseDocumentLock(targetDoc.id);
      }
      toast({
        variant: 'destructive',
        title: 'Retry Failed',
        description: err instanceof Error ? err.message : 'Failed to retry plan execution.',
      });
    } finally {
      setPlanProcessing(false);
    }
  };

  const handleAbortPlan = async () => {
    if (!document.projectId) return;
    setPlanProcessing(true);
    try {
      // Fetch the latest version first to avoid 409 Conflict
      // During plan execution, the backend updates the document, making our version stale
      const freshDoc = await documentsApi.loadAsJson(
        document.projectId,
        document.documentNumber
      );
      
      if (!freshDoc) {
        logger.error('[DocumentViewerContent] Failed to fetch fresh document for abort');
        return;
      }
      
      const updatedDocument = {
        ...freshDoc,
        metadata: {
          ...freshDoc.metadata,
          planStatus: 'failed' as const, // Abort sets to failed
        },
      };
      
      await documentsApi.saveJsonDocument(
        document.projectId,
        document.documentNumber,
        updatedDocument
      );
      
      await loadDocument();
      logger.debug('[DocumentViewerContent] Plan aborted');
    } catch (err) {
      logger.error('Failed to abort plan:', err);
    } finally {
      setPlanProcessing(false);
    }
  };

  // Common header component for all states
  const DocumentHeader = () => (
    <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
      <h2 className="text-sm font-medium text-foreground truncate flex-1">
        {document.documentNumber} - {document.title}
      </h2>
      
      {/* Export buttons - only for project documents with JSON content */}
      {document.type === 'project' && jsonDocument && document.projectId && document.documentNumber && (
        <div className="flex items-center gap-2">
          {/* Word Export */}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportWord}
            disabled={exportingWord}
            className="text-xs h-7"
            title="Export to Word"
          >
            {exportingWord ? (
              <>
                <Spinner size="sm" className="mr-1" />
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Word
              </>
            )}
          </Button>
          
          {/* PDF Export */}
          <PDFExportButton
            projectId={document.projectId}
            documentNumber={document.documentNumber}
            documentTitle={jsonDocument.metadata?.documentTitle || document.title}
            revision={jsonDocument.metadata?.currentRevision || 'Draft'}
            className="text-xs h-7"
            onOpenPdfInViewer={onOpenPdfInViewer}
          />
        </div>
      )}
      
      <span className={`text-xs px-2 py-0.5 rounded border ${
        document.type === 'project' ? 'bg-transparent border-info/50 text-info' :
        document.type === 'company' ? 'bg-transparent border-border/50 text-muted-foreground' :
        document.type === 'standard' ? 'bg-transparent border-warning/50 text-warning' :
        'bg-transparent border-border text-muted-foreground'
      }`}>
        {document.type === 'standard' ? 'Reference' : document.type === 'project' ? 'Project' : document.type === 'company' ? 'Company' : document.type}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <DocumentHeader />
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="relative mb-4">
              {/* Animated loading spinner */}
              <div className="w-16 h-16 border-4 border-border border-t-purple-600 dark:border-t-purple-400 rounded-full animate-spin mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl">{document.type === 'standard' ? '📄' : '📋'}</span>
              </div>
            </div>
            <div className="text-lg font-medium text-foreground mb-1">
              {document.type === 'standard' ? 'Loading PDF...' : 'Loading document...'}
            </div>
            <div className="text-sm text-muted-foreground max-w-xs truncate">
              {document.title}
            </div>
            {document.type === 'standard' && (
              <div className="text-xs text-muted-foreground mt-2">
                Large documents may take a moment to download
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <DocumentHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-destructive mb-2">❌ Error Loading Document</div>
            <div className="text-sm text-muted-foreground">{error}</div>
            <Button
              variant="secondary"
              size="sm"
              onClick={loadDocument}
              className="mt-4"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Design documents - external files with no built-in viewer
  const isDesignDoc = document.id?.startsWith('design-');
  if (isDesignDoc && !jsonDocument && !pdfUrl) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <DocumentHeader />
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-center max-w-md p-8">
            <div className="text-5xl mb-4">📐</div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Design Document
            </h3>
            <p className="text-muted-foreground mb-4">
              {document.documentNumber}
            </p>
            <div className="bg-warning-light border border-warning rounded-lg p-4">
              <p className="text-sm text-warning">
                <span className="font-medium">External File</span><br/>
                This design document is stored in an external system (PDM, Altium, etc.). 
                No built-in viewer is available.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Company documents without JSON content (PDF or other format)
  if (companyDocMeta && !jsonDocument && !pdfUrl) {
    const formatDisplay = companyDocMeta.format || 'Unknown';
    const isPdf = formatDisplay.toLowerCase() === 'pdf' || companyDocMeta.s3Key?.endsWith('.pdf');
    
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <DocumentHeader />
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-center max-w-md p-8">
            <div className="text-5xl mb-4">{isPdf ? '📄' : '📁'}</div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {companyDocMeta.title || document.title}
            </h3>
            <p className="text-muted-foreground mb-2">
              {document.documentNumber}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Rev {companyDocMeta.currentRevision ?? '--'} • {companyDocMeta.status || 'Draft'}
            </p>
            <div className="bg-info-light border border-info rounded-lg p-4">
              <p className="text-sm text-info">
                <span className="font-medium">{isPdf ? 'PDF Document' : `${formatDisplay} Format`}</span><br/>
                This document is stored as {isPdf ? 'a PDF file' : `a ${formatDisplay} file`} and does not have a built-in viewer.
                {companyDocMeta.category && (
                  <span className="block mt-2 text-xs opacity-75">
                    Category: {companyDocMeta.category}
                    {companyDocMeta.subcategory && ` / ${companyDocMeta.subcategory}`}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Document Header with Back Button */}
      <DocumentHeader />

      {/* Save Conflict Error Banner */}
      {saveConflictError && (
        <div className="flex-shrink-0 bg-destructive-light border-b-2 border-destructive px-6 py-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-base">❌</span>
              <div>
                <div className="text-sm font-semibold text-destructive">
                  Save Failed
                </div>
                <div className="text-xs text-destructive">
                  {saveConflictError}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => window.location.reload()}
                variant="destructive"
                size="xs"
              >
                Reload Page
              </Button>
              <Button
                onClick={() => setSaveConflictError(null)}
                variant="outline"
                size="xs"
                className="border-destructive text-destructive"
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Concurrent Editor Warning Banner */}
      {isEditing && hasConflict && showConflictWarning && (
        <div className="flex-shrink-0 bg-warning-light border-b-2 border-warning px-6 py-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-base">⚠️</span>
              <div>
                <div className="text-sm font-semibold text-warning">
                  Other users are editing this document
                </div>
                <div className="text-xs text-warning">
                  {otherEditors.map(e => e.userName || e.userEmail).join(', ')} 
                  {otherEditors.length === 1 ? ' is' : ' are'} also editing. 
                  Changes may conflict.
                </div>
              </div>
            </div>
            <Button
              onClick={() => setShowConflictWarning(false)}
              variant="outline"
              size="xs"
              className="border-warning text-warning"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Edit Mode Banner - Enhanced with visual feedback */}
      {isEditing && (
        <div className="flex-shrink-0 bg-success-light border-b-2 border-success px-6 py-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-base">✏️</span>
              <div>
                <div className="text-sm font-semibold text-success">
                  Editing: {document.title}
                </div>
                <div className="text-xs text-success">
                  AI Agent will edit this document
                  {hasConflict && <span className="ml-2 text-warning">• {otherEditors.length} other editor{otherEditors.length > 1 ? 's' : ''}</span>}
                </div>
              </div>
            </div>
            <Button
              onClick={handleDisableEdit}
              variant="secondary"
              size="xs"
            >
              Stop Editing
            </Button>
          </div>
        </div>
      )}

      {/* Plan Control Bar (for ai_plan documents) */}
      {!isEditing && isPlanDocument && document.projectId && (
        <PlanControlBar
          documentNumber={document.documentNumber}
          projectId={document.projectId}
          planStatus={planStatus as 'draft' | 'ready' | 'executing' | 'paused' | 'completed' | 'failed'}
          targetDocument={targetDocument}
          taskCount={taskCount}
          completedTaskCount={completedTaskCount}
          onLinkDocument={() => setShowLinkTargetModal(true)}
          onUnlinkDocument={handleUnlinkTargetDocument}
          onMarkReady={handleMarkReady}
          onRevokeReady={handleRevokeReady}
          onExecute={handleExecutePlan}
          onPause={handlePausePlan}
          onResume={handleResumePlan}
          onRetry={handleRetryPlan}
          onAbort={handleAbortPlan}
          onEnableEdit={document.canEdit ? onEnableEdit : undefined}
          canEdit={document.canEdit}
          isProcessing={planProcessing}
        />
      )}

      {/* Document Control Bar (status, revisions, edit controls) - for non-plan documents */}
      {!isEditing && !isPlanDocument && (document.type === 'project' || document.type === 'company') && (
        <DocumentControlBar
          documentNumber={document.documentNumber}
          projectId={document.projectId}
          documentType={document.type}
          currentStatus={documentStatus}
          currentRevision={currentRevision}
          revisions={revisions}
          onRefresh={loadDocument}
          onEnableEdit={document.canEdit ? onEnableEdit : undefined}
          canEdit={document.canEdit}
        />
      )}

      {/* Document Content */}
      <div className="flex-1 overflow-y-auto bg-background">
        {pdfUrl && document.type === 'standard' ? (
          // PDF Viewer for reference documents
          <PdfViewer pdfUrl={pdfUrl} documentTitle={document.title} />
        ) : isEditing && jsonDocument ? (
          // Editable mode - use StructuredEditor (works for project and company docs)
          <StructuredEditor
            document={jsonDocument}
            onUpdate={handleUpdate}
            onSave={handleSave}
            readOnly={false}
            hasUnsavedChanges={hasUnsavedChanges}
            saving={saving}
            lastSaved={lastSaved}
            isOnline={isOnline}
            editorRef={editorRef}
            projectId={document.projectId}
            documentNumber={document.documentNumber}
            streamingOperations={streamingOperations}
            onDocumentSyncFromServer={(serverDocument, newS3VersionId, preAIVersionId) => {
              // CRITICAL: Update local state with server's document
              // The backend already saved this document, so we just sync our state
              logger.debug('[DocumentViewerContent] Received document sync from server, updating state');
              logger.debug('[DocumentViewerContent] Server document version:', serverDocument.version, 's3VersionId:', newS3VersionId, 'preAIVersionId:', preAIVersionId);
              
              // Clear AI streaming flag - document_sync means the AI edit cycle is complete
              // This re-enables auto-save timers for subsequent user edits
              isAIStreamingRef.current = false;
              
              setJsonDocument(serverDocument);
              setVersion(serverDocument.version);
              versionRef.current = serverDocument.version; // Update ref immediately to avoid stale closures
              setLastSaved(new Date()); // Server just saved it
              setHasUnsavedChanges(false); // No need to save - server already did
              
              // Update s3VersionId for future AI undo operations
              if (newS3VersionId) {
                setS3VersionId(newS3VersionId);
                // Notify parent to update openedDocuments with new s3VersionId and s3JsonKey
                if (onDocumentLoaded) {
                  onDocumentLoadedRef.current?.(serverDocument, serverDocument.version, newS3VersionId, s3JsonKey);
                }
              }
              
              // Clear any pending auto-save timer since server already saved
              if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
              }
            }}
            onStreamingComplete={() => {
              logger.debug('[DocumentViewerContent] Streaming complete, clearing queue');
              operationQueueRef.current = []; // Clear the ref too!
              setStreamingOperations([]);
              
              // NOTE: No auto-save triggered here!
              // The backend already saved the document when it applied the operations.
              // onDocumentSyncFromServer already updated our local state.
            }}
            onOpenPdfInViewer={onOpenPdfInViewer}
          />
        ) : jsonDocument ? (
          // Read-only mode for JSON documents - use StructuredEditor in read-only mode
          <StructuredEditor
            document={jsonDocument}
            onUpdate={() => {}}
            onSave={() => {}}
            readOnly={true}
          />
        ) : htmlContent ? (
          // Fallback HTML content
          <div
            className="p-8 prose max-w-none"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : null}
      </div>

      {/* Link Target Document Modal (for plans) */}
      {document.projectId && (
        <LinkTargetDocumentModal
          isOpen={showLinkTargetModal}
          onClose={() => setShowLinkTargetModal(false)}
          onSelect={handleLinkTargetDocument}
          projectId={document.projectId}
          excludeDocumentNumber={document.documentNumber}
        />
      )}

    </div>
  );
}
